import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';
import { Menu, MenuDivider, MenuItem, MenuSegmentTitle } from 'carbon-react/lib/components/menu';

import { Consolidation } from '@futrli-sdk/consolidations';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import {
  getEditConsolidationUrl,
  getSfaOnboardingUrl,
  matchSettingsPath,
  storeCurrentLocation
} from '@futrli/shared-services';
import { getWorkspaceListingUrl, getWorkspaceUrl } from '@futrli/predict-next/workspaces/utils/route-utils';
import { OrganisationListItem } from '@futrli-sdk/workspaces';
import { UIProductIcon } from '@futrli/ui-components';

import { EXIT_SETTINGS_KEY } from '../../../../../../skeleton/src/core/constants';

import * as styles from './organisations-menu.scss';

interface Props {
  readonly hasMembersPermission: boolean;
  organisations: (OrganisationListItem | Consolidation)[];
}

export const OrganisationsMenu = ({ hasMembersPermission, organisations }: Props) => {
  const [menuKey, setMenuKey] = useState(0);
  const { workspaceId: workspaceIdParam, organisationId: organisationIdParam } = useParams();
  const workspaceId = workspaceIdParam || '';
  const organisationId = organisationIdParam || '';
  const navigate = useNavigate();
  const intl = useIntl();
  const location = useLocation();

  const organisation = organisations.find(o => o.id === organisationId);

  const handleConsolidationSettingsClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    e.stopPropagation();
    navigate(getEditConsolidationUrl(workspaceId, id));
  };

  const storeExitLocation = () => {
    // Let's not store the exit link if we're already in settings
    if (!matchSettingsPath(location.pathname)) {
      storeCurrentLocation(EXIT_SETTINGS_KEY);
    }
  };

  const handleOrganisationsClick = () => {
    storeExitLocation();
    navigate(getWorkspaceListingUrl(workspaceId));
  };

  const handleSwitchOrganisationClick = (o: OrganisationListItem | Consolidation) => {
    setMenuKey(prev => prev + 1); // force remount to close dropdown
    const bid = 'sxp_business_id' in o ? o.sxp_business_id || '' : '';
    if ('display_currency' in o) {
      navigate(`/workspaces/${workspaceId}/consolidations/${o.id}`);
    } else if ('is_holding' in o && o.is_holding) {
      navigate(getSfaOnboardingUrl(bid));
    } else {
      navigate(`/workspaces/${workspaceId}/organisations/${o.id}${bid ? `?bid=${bid}` : ''}`);
    }
  };

  const handleUsersClick = () => {
    storeExitLocation();
    navigate(getWorkspaceUrl(workspaceId, 'users'));
  };

  const getOrganisationName = (organisation?: OrganisationListItem | Consolidation) => {
    if (organisation) {
      const isConsolidation = 'display_currency' in organisation;
      const consolidationSuffix = intl.formatMessage({
        id: 'common.consolidated_suffix',
        defaultMessage: ' (consolidated)'
      });
      return `${organisation?.name}${isConsolidation ? consolidationSuffix : ''}`;
    }
  };

  if (!organisationId || !organisation) {
    return null;
  }

  return (
    <Box>
      <Menu menuType="black" key={menuKey}>
        <MenuItem
          submenu={getOrganisationName(organisation)}
          minWidth={100}
          maxWidth={700}
          submenuDirection="right"
          submenuMinWidth="150px"
          submenuMaxWidth="800px"
        >
          <MenuItem onClick={handleOrganisationsClick}>
            <FormattedMessage id="common.manage_organisations" defaultMessage="Manage Organisations" />
          </MenuItem>
          {hasMembersPermission ? (
            <MenuItem onClick={handleUsersClick}>
              <FormattedMessage id="common.manage_users" defaultMessage="Manage Users" />
            </MenuItem>
          ) : null}

          {organisations.length > 1 && (
            <div style={{ maxHeight: 418 }}>
              <MenuDivider />
              <MenuSegmentTitle
                text={intl.formatMessage({ id: 'common.switch_business', defaultMessage: 'Switch business' })}
              >
                {[...organisations]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(o => (
                    <div key={o.id} className={styles.menuItemParent}>
                      <MenuItem onClick={() => handleSwitchOrganisationClick(o)}>
                        <div className={styles.menuItemContent}>
                          <div>{getOrganisationName(o)}</div>
                          {'display_currency' in o ? (
                            <div className={styles.cogButton} onClick={e => handleConsolidationSettingsClick(e, o.id)}>
                              <UIProductIcon iconName="predict-settings" className={styles.uiMenuSettingsIcon} />
                            </div>
                          ) : (
                            <div />
                          )}
                        </div>
                      </MenuItem>
                    </div>
                  ))}
              </MenuSegmentTitle>
            </div>
          )}
        </MenuItem>
      </Menu>
    </Box>
  );
};
